import axios from 'axios'
import forge from 'node-forge'
import { baseUrl } from '@/config/config'

const SECURITY_META_PATH = '/h5/v1/resume/securityMeta'
const SECURITY_META_STORAGE_KEY = 'jemai_h5_security_meta_v1'
const DEFAULT_ENC_VERSION = 'v1'

const metaRequest = axios.create({
  baseURL: baseUrl,
  timeout: 15000,
})

let securityMetaCache = null
let securityMetaPromise = null
let publicKeyCache = null
let publicKeySource = ''

export async function encryptH5Payload(payload, requestInfo = {}) {
  const meta = await getH5SecurityMeta()
  const path = normalizePath(requestInfo.url)
  const method = (requestInfo.method || 'POST').toUpperCase()
  const ts = Math.floor(Date.now() / 1000)
  const nonce = bytesToHex(randomBytes(12))
  const rawAesKey = randomBytes(32)
  const iv = randomBytes(12)
  const plainText = JSON.stringify(payload || {})
  const aadText = buildRequestAAD(method, path, ts, nonce)
  const cryptoImpl = getCryptoImpl()
  const encryptedBody = await cryptoImpl.encryptBody(rawAesKey, iv, plainText, aadText)
  const encryptedKey = await cryptoImpl.encryptKey(meta.public_key, rawAesKey, aadText)

  return {
    envelope: {
      enc_ver: meta.enc_version || DEFAULT_ENC_VERSION,
      ts,
      nonce,
      ek: bufferToBase64(encryptedKey),
      iv: bufferToBase64(iv),
      data: bufferToBase64(encryptedBody),
    },
    context: {
      aesKey: bufferToBase64(rawAesKey),
      path,
      ts,
      nonce,
    },
  }
}

export async function decryptH5Payload(payload, requestContext = {}) {
  if (!payload || !payload.iv || !payload.data) {
    return payload
  }
  if (!requestContext.aesKey || !requestContext.path || !requestContext.ts || !requestContext.nonce) {
    throw new Error('缺少接口解密上下文')
  }
  const cryptoImpl = getCryptoImpl()
  const plainText = await cryptoImpl.decryptBody(
    base64ToUint8Array(requestContext.aesKey),
    base64ToUint8Array(payload.iv),
    base64ToUint8Array(payload.data),
    buildResponseAAD(requestContext.path, requestContext.ts, requestContext.nonce)
  )
  return JSON.parse(plainText)
}

async function getH5SecurityMeta() {
  if (securityMetaCache) {
    return securityMetaCache
  }
  const cached = loadCachedMeta()
  if (cached) {
    securityMetaCache = cached
    return cached
  }
  if (!securityMetaPromise) {
    securityMetaPromise = metaRequest.get(SECURITY_META_PATH).then(response => {
      const payload = response.data || {}
      if (payload.code !== 200 || !payload.data || !payload.data.public_key) {
        throw new Error(payload.msg || '获取接口加密配置失败')
      }
      securityMetaCache = payload.data
      cacheMeta(payload.data)
      return payload.data
    }).finally(() => {
      securityMetaPromise = null
    })
  }
  return securityMetaPromise
}

function buildRequestAAD(method, path, ts, nonce) {
  return `h5-api|${DEFAULT_ENC_VERSION}|${method}|${path}|${ts}|${nonce}`
}

function buildResponseAAD(path, ts, nonce) {
  return `h5-api|${DEFAULT_ENC_VERSION}|response|${path}|${ts}|${nonce}`
}

function normalizePath(url = '') {
  if (/^https?:\/\//i.test(url)) {
    return new URL(url).pathname
  }
  return url.startsWith('/') ? url : `/${url}`
}

function randomBytes(size) {
  if (!supportsWebCrypto()) {
    return binaryToUint8Array(forge.random.getBytesSync(size))
  }
  const bytes = new Uint8Array(size)
  window.crypto.getRandomValues(bytes)
  return bytes
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')
}

function bufferToBase64(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return window.btoa(binary)
}

function base64ToUint8Array(value) {
  return new Uint8Array(base64ToArrayBuffer(value))
}

function base64ToArrayBuffer(value) {
  const binary = window.atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function encodeText(value) {
  return new TextEncoder().encode(value)
}

function decodeText(buffer) {
  return new TextDecoder().decode(buffer)
}

function supportsWebCrypto() {
  return !!(window.crypto && window.crypto.subtle && window.TextEncoder && window.TextDecoder)
}

function getCryptoImpl() {
  return supportsWebCrypto() ? webCryptoImpl : forgeCryptoImpl
}

function loadCachedMeta() {
  try {
    const raw = window.sessionStorage.getItem(SECURITY_META_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.public_key) {
      return null
    }
    return parsed
  } catch (error) {
    return null
  }
}

function cacheMeta(meta) {
  try {
    window.sessionStorage.setItem(SECURITY_META_STORAGE_KEY, JSON.stringify(meta))
  } catch (error) {
    // ignore storage errors
  }
}

const webCryptoImpl = {
  async encryptKey(publicKeyPem, rawAesKey, aadText) {
    const publicKey = await getWebCryptoPublicKey(publicKeyPem)
    return window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
        label: encodeText(aadText),
      },
      publicKey,
      rawAesKey
    )
  },
  async encryptBody(rawAesKey, iv, plainText, aadText) {
    const aesKey = await window.crypto.subtle.importKey('raw', rawAesKey, 'AES-GCM', false, ['encrypt'])
    return window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData: encodeText(aadText),
      },
      aesKey,
      encodeText(plainText)
    )
  },
  async decryptBody(rawAesKey, iv, cipherText, aadText) {
    const aesKey = await window.crypto.subtle.importKey('raw', rawAesKey, 'AES-GCM', false, ['decrypt'])
    const plainBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData: encodeText(aadText),
      },
      aesKey,
      cipherText
    )
    return decodeText(plainBuffer)
  },
}

const forgeCryptoImpl = {
  async encryptKey(publicKeyPem, rawAesKey, aadText) {
    const publicKey = getForgePublicKey(publicKeyPem)
    const encrypted = publicKey.encrypt(uint8ArrayToBinary(rawAesKey), 'RSA-OAEP', {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha256.create(),
      },
      label: aadText,
    })
    return binaryToUint8Array(encrypted)
  },
  async encryptBody(rawAesKey, iv, plainText, aadText) {
    const cipher = forge.cipher.createCipher('AES-GCM', uint8ArrayToBinary(rawAesKey))
    cipher.start({
      iv: uint8ArrayToBinary(iv),
      additionalData: aadText,
      tagLength: 128,
    })
    cipher.update(forge.util.createBuffer(plainText, 'utf8'))
    if (!cipher.finish()) {
      throw new Error('接口数据加密失败')
    }
    const encrypted = cipher.output.getBytes() + cipher.mode.tag.getBytes()
    return binaryToUint8Array(encrypted)
  },
  async decryptBody(rawAesKey, iv, cipherText, aadText) {
    const cipherBinary = uint8ArrayToBinary(cipherText)
    const dataBinary = cipherBinary.slice(0, -16)
    const tagBinary = cipherBinary.slice(-16)
    const decipher = forge.cipher.createDecipher('AES-GCM', uint8ArrayToBinary(rawAesKey))
    decipher.start({
      iv: uint8ArrayToBinary(iv),
      additionalData: aadText,
      tagLength: 128,
      tag: forge.util.createBuffer(tagBinary),
    })
    decipher.update(forge.util.createBuffer(dataBinary))
    if (!decipher.finish()) {
      throw new Error('接口数据解密失败')
    }
    return decipher.output.toString('utf8')
  },
}

async function getWebCryptoPublicKey(publicKeyPem) {
  if (publicKeyCache && publicKeySource === publicKeyPem) {
    return publicKeyCache
  }
  const pemBody = (publicKeyPem || '')
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '')
  const keyData = base64ToArrayBuffer(pemBody)
  publicKeyCache = await window.crypto.subtle.importKey(
    'spki',
    keyData,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  )
  publicKeySource = publicKeyPem
  return publicKeyCache
}

function getForgePublicKey(publicKeyPem) {
  if (publicKeyCache && publicKeySource === publicKeyPem) {
    return publicKeyCache
  }
  publicKeyCache = forge.pki.publicKeyFromPem(publicKeyPem)
  publicKeySource = publicKeyPem
  return publicKeyCache
}

function uint8ArrayToBinary(value) {
  return Array.from(value || [])
    .map(byte => String.fromCharCode(byte))
    .join('')
}

function binaryToUint8Array(binary) {
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}
